import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Clock, ShieldCheck, Upload, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function ContactPage() {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    telefon: '',
    emne: 'Generel forespørgsel',
    besked: '',
    website_url: '' // Honeypot field
  });

  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submissionId, setSubmissionId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    // Validation rules
    const maxFiles = 5;
    const maxSingleSize = 10 * 1024 * 1024; // 10 MB
    const maxTotalSize = 20 * 1024 * 1024; // 20 MB
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];

    const messages = {
      maxFiles: {
        da: `Du kan højst vedhæfte ${maxFiles} filer i alt.`,
        en: `You can attach a maximum of ${maxFiles} files in total.`,
        de: `Sie können insgesamt maximal ${maxFiles} Dateien anhängen.`
      },
      invalidType: {
        da: (name) => `Filtypen for "${name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP understøttes.`,
        en: (name) => `File type for "${name}" is not allowed. Only PDF, PNG, JPEG, GIF, and WEBP are supported.`,
        de: (name) => `Dateityp für "${name}" ist nicht erlaubt. Nur PDF, PNG, JPEG, GIF und WEBP werden unterstützt.`
      },
      fileTooLarge: {
        da: (name) => `Filen "${name}" er for stor. Maksimal størrelse pr. fil er 10 MB.`,
        en: (name) => `The file "${name}" is too large. Maximum size per file is 10 MB.`,
        de: (name) => `Die Datei "${name}" ist zu groß. Die maximale Größe pro Datei beträgt 10 MB.`
      },
      totalSizeExceeded: {
        da: `Den samlede størrelse af alle filer overstiger grænsen på 20 MB.`,
        en: `The total size of all files exceeds the limit of 20 MB.`,
        de: `Die Gesamtgröße aller Dateien überschreitet das Limit von 20 MB.`
      }
    };

    const activeLang = language || 'da';

    if (files.length + selectedFiles.length > maxFiles) {
      alert(messages.maxFiles[activeLang] || messages.maxFiles.da);
      return;
    }

    let currentTotalSize = files.reduce((sum, f) => sum + f.size, 0);
    const validFiles = [];

    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        const fn = messages.invalidType[activeLang] || messages.invalidType.da;
        alert(fn(file.name));
        continue;
      }

      if (file.size > maxSingleSize) {
        const fn = messages.fileTooLarge[activeLang] || messages.fileTooLarge.da;
        alert(fn(file.name));
        continue;
      }

      if (currentTotalSize + file.size > maxTotalSize) {
        alert(messages.totalSizeExceeded[activeLang] || messages.totalSizeExceeded.da);
        break;
      }

      currentTotalSize += file.size;
      validFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Use FormData to support file uploads
      const data = new FormData();
      data.append('navn', formData.navn);
      data.append('email', formData.email);
      data.append('telefon', formData.telefon);
      data.append('emne', formData.emne);
      data.append('besked', formData.besked);
      data.append('website_url', formData.website_url); // Honeypot

      // Append files
      files.forEach((file) => {
        data.append('filer', file);
      });

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data
        // Let browser set Content-Type for multipart/form-data
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success');
        setSubmissionId(result.submissionId);
        // Reset form
        setFormData({
          navn: '',
          email: '',
          telefon: '',
          emne: 'Generel forespørgsel',
          besked: '',
          website_url: ''
        });
        setFiles([]);
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || t('contact.form_error'));
      }
    } catch (error) {
      setSubmitStatus('error');
      const networkErrorMsg = {
        da: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.',
        en: 'Network error. Please check your internet connection and try again.',
        de: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
      };
      setErrorMessage(networkErrorMsg[language || 'da'] || networkErrorMsg.da);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-44 md:pt-52 pb-20 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header Section */}
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-black italic text-[#293e30] uppercase tracking-tight mb-4 leading-tight">
            {t('contact.title')}
          </h1>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed">
            {t('contact.desc')}
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* Contact Details Card */}
            <div className="bg-white p-8 border border-slate-100 shadow-xl shadow-slate-100/50 rounded-none space-y-8">
              <h2 className="text-xl font-black italic text-[#293e30] uppercase tracking-wider border-b border-slate-100 pb-4">
                {t('contact.direct')}
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="text-[#293e30] shrink-0 pt-1">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <a href="mailto:info@vejleautoexport.dk" className="text-slate-800 hover:text-[#293e30] font-semibold text-base transition-colors">
                      info@vejleautoexport.dk
                    </a>
                    <p className="text-xs text-slate-500 mt-1">{t('contact.email_desc')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-[#293e30] shrink-0 pt-1">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <a href="tel:+4560133473" className="text-slate-800 hover:text-[#293e30] font-semibold text-base transition-colors">
                      60 13 34 73
                    </a>
                    <p className="text-xs text-slate-500 mt-1">{t('contact.phone_desc')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-[#293e30] shrink-0 pt-1">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-slate-800 font-semibold text-base">
                      Vejle AutoExport
                    </p>
                    <p className="text-sm text-slate-600">Storgaden 97, 6052 Viuf</p>
                    <p className="text-xs text-slate-500 mt-1">CVR-nr: 30158628</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours Card */}
            <div className="bg-white p-8 border border-slate-100 shadow-xl shadow-slate-100/50 rounded-none space-y-6">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <Clock className="h-5 w-5 text-[#293e30]" />
                <h2 className="text-lg font-black italic text-[#293e30] uppercase tracking-wider">
                  {t('contact.hours')}
                </h2>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center py-1 border-b border-slate-50">
                  <span className="text-slate-600 font-medium">{t('contact.hours_weekdays')}</span>
                  <span className="text-slate-900 font-bold">10:00 - 17:30</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-slate-600 font-medium">{t('contact.hours_weekend')}</span>
                  <span className="text-slate-500 font-medium">{t('contact.hours_weekend_desc')}</span>
                </div>
              </div>
            </div>

            {/* Trust Badge Card */}
            <div className="bg-[#293e30] text-white p-8 border border-slate-800 rounded-none flex items-start gap-4">
              <ShieldCheck className="h-8 w-8 text-white shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-black italic uppercase tracking-wider mb-2">{t('contact.badge_title')}</h3>
                <p className="text-[#f1f1f1] text-sm leading-relaxed">
                  {t('contact.badge_desc')}
                </p>
              </div>
            </div>

          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white p-8 md:p-10 border border-slate-200 shadow-2xl shadow-slate-300/60 rounded-none">
              
              {submitStatus === 'success' ? (
                <div className="text-center py-8 space-y-6 animate-fade-in">
                  <div className="inline-flex items-center justify-center h-16 w-16 bg-emerald-50 text-emerald-500 rounded-none">
                    <CheckCircle2 className="h-10 w-10" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-black italic text-[#293e30] uppercase tracking-tight">
                      {t('contact.form_success_title')}
                    </h2>
                    <p className="text-slate-600 max-w-md mx-auto">
                      {t('contact.form_success_desc')}
                    </p>
                  </div>
                  {submissionId && (
                    <div className="bg-slate-50 p-4 inline-block border border-slate-100 text-xs text-slate-500">
                      {t('contact.form_ref_id')} <span className="font-mono font-bold text-slate-700">{submissionId}</span>
                    </div>
                  )}
                  <div>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="inline-flex items-center justify-center px-6 py-3 bg-black hover:bg-slate-900 text-white font-bold text-sm tracking-wider uppercase rounded-none transition-all cursor-pointer"
                    >
                      {t('contact.form_new_message')}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Honeypot field (STRICTLY HIDDEN FROM HUMANS) */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Honeypot</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <h2 className="text-2xl font-black italic text-[#293e30] uppercase tracking-tight mb-6">
                    {t('contact.form_title')}
                  </h2>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-none flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                      <p className="text-sm">{errorMessage}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <label htmlFor="navn" className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        {t('contact.form_name')}
                      </label>
                      <input
                        id="navn"
                        type="text"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleInputChange}
                        className="w-full h-12 px-4 border border-slate-200 rounded-none text-slate-900 bg-white focus:border-[#293e30] focus:ring-1 focus:ring-[#293e30] outline-none transition-all text-sm"
                        placeholder={t('contact.form_name_placeholder')}
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <label htmlFor="telefon" className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        {t('contact.form_phone')}
                      </label>
                      <input
                        id="telefon"
                        type="tel"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleInputChange}
                        className="w-full h-12 px-4 border border-slate-200 rounded-none text-slate-900 bg-white focus:border-[#293e30] focus:ring-1 focus:ring-[#293e30] outline-none transition-all text-sm"
                        placeholder={t('contact.form_phone_placeholder')}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      {t('contact.form_email')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full h-12 px-4 border border-slate-200 rounded-none text-slate-900 bg-white focus:border-[#293e30] focus:ring-1 focus:ring-[#293e30] outline-none transition-all text-sm"
                      placeholder={t('contact.form_email_placeholder')}
                    />
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <label htmlFor="emne" className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      {t('contact.form_subject_label')}
                    </label>
                    <select
                      id="emne"
                      name="emne"
                      value={formData.emne}
                      onChange={handleInputChange}
                      className="w-full h-12 px-4 border border-slate-200 rounded-none text-slate-900 bg-white focus:border-[#293e30] focus:ring-1 focus:ring-[#293e30] outline-none transition-all text-sm appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                        backgroundSize: '1.25em'
                      }}
                    >
                      <option value="Generel forespørgsel">{t('contact.form_subject_opt3')}</option>
                      <option value="Spørgsmål til vurdering">{t('contact.form_subject_opt5')}</option>
                      <option value="Bilsalg til eksport">{t('contact.form_subject_opt2')}</option>
                      <option value="Andet">{t('contact.form_subject_opt4')}</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label htmlFor="besked" className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      {t('contact.form_message')}
                    </label>
                    <textarea
                      id="besked"
                      name="besked"
                      required
                      rows="5"
                      value={formData.besked}
                      onChange={handleInputChange}
                      className="w-full p-4 border border-slate-200 rounded-none text-slate-900 bg-white focus:border-[#293e30] focus:ring-1 focus:ring-[#293e30] outline-none transition-all text-sm resize-none"
                      placeholder={t('contact.form_message_placeholder')}
                    />
                  </div>

                  {/* File Upload (Optional) */}
                  <div className="space-y-2">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                      {t('contact.form_upload_label')}
                    </span>
                    <p className="text-xs text-slate-500 mb-2">
                      {t('contact.form_upload_desc')}
                    </p>
                    
                    <div className="border-2 border-dashed border-slate-200 hover:border-[#293e30] transition-colors p-6 text-center cursor-pointer relative bg-slate-50/50">
                      <input
                        id="filer"
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-slate-700">{t('contact.form_upload_click')}</p>
                      <p className="text-xs text-slate-500 mt-1">{t('contact.form_upload_drag')}</p>
                    </div>

                    {/* Selected Files List */}
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">{t('contact.form_upload_selected')} ({files.length}):</p>
                        <div className="grid grid-cols-1 gap-2">
                          {files.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 text-sm rounded-none">
                              <span className="font-medium text-slate-700 truncate max-w-[80%]">{file.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="text-slate-400 hover:text-red-500 transition-colors p-1"
                                  aria-label="Fjern fil"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 py-4 rounded-none bg-black hover:bg-slate-900 text-white font-bold text-base uppercase tracking-wider transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          {t('contact.form_submitting')}
                        </>
                      ) : (
                        t('contact.form_submit')
                      )}
                    </button>
                  </div>

                </form>
              )}

            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
